import apiClient from './client';
import { ApiResponse } from '@/types';

/**
 * GitLab User
 */
export interface GitLabUser {
  id: number;
  username: string;
  name: string;
}

/**
 * Issue summary for list view
 */
export interface IssueSummary {
  id: number;
  iid: number;
  title: string;
  state: 'opened' | 'closed';
  // The workflow status from the labels, as on the board: todo, in-progress, in-review, test, needs-info, …
  status?: string;
  labels: string[];
  assignees: GitLabUser[];
  author: GitLabUser;
  createdAt: string;
  updatedAt: string;
  dueDate: string | null;
  webUrl: string;
  repoName: string;
  repoId: string;
  isStale: boolean;
  daysSinceCreated: number;
  daysSinceUpdated: number;
  milestone: { title: string; dueDate: string | null } | null;
}

/**
 * Merge request summary
 */
export interface MergeRequestSummary {
  id: number;
  iid: number;
  title: string;
  state: 'opened' | 'closed' | 'merged' | 'locked';
  sourceBranch: string;
  targetBranch: string;
  author: GitLabUser;
  assignees: GitLabUser[];
  labels: string[];
  createdAt: string;
  updatedAt: string;
  mergedAt: string | null;
  webUrl: string;
  repoName: string;
  repoId: string;
  isDraft: boolean;
  hasConflicts: boolean;
  notesCount: number;
  daysSinceCreated: number;
  approvedBy?: string[]; // open MRs: who approved (rv_qa after a QA pass)
}

/**
 * Repo stats
 */
export interface RepoStats {
  id: string;
  name: string;
  gitlabUrl: string;
  issueCount: number;
  mrCount: number;
  lastCommitAt: string | null;
}

/**
 * Project dashboard summary
 */
export interface DashboardSummary {
  totalIssues: number;
  openIssues: number;
  staleIssues: number;
  totalMergeRequests: number;
  openMergeRequests: number;
  draftMergeRequests: number;
  conflictMergeRequests: number;
  recentCommits: number;
  recentReviews: number;
  avgReviewScore: number;
}

/**
 * Stale issue
 */
export interface StaleIssue {
  id: number;
  iid: number;
  title: string;
  assignees: GitLabUser[];
  webUrl: string;
  repoName: string;
  daysSinceCreated: number;
  daysSinceUpdated: number;
}

/**
 * Issue by assignee
 */
export interface IssueByAssignee {
  user: GitLabUser;
  count: number;
  issues: Array<{
    iid: number;
    title: string;
    webUrl: string;
    isStale: boolean;
  }>;
}

/**
 * Recent commit
 */
export interface RecentCommit {
  sha: string;
  shortSha: string;
  title: string;
  author: {
    _id: string;
    username: string;
    displayName: string;
  } | null;
  committedAt: string;
  webUrl: string;
  additions: number;
  deletions: number;
}

/**
 * Recent review
 */
export interface RecentReview {
  id: string;
  commitSha: string;
  summary: string;
  overallScore: number;
  issueCount: number;
  createdAt: string;
  userId: {
    _id: string;
    username: string;
    displayName: string;
  } | null;
}

/**
 * Project info
 */
export interface ProjectInfo {
  id: string;
  name: string;
  code: string;
  description?: string;
  manager: {
    _id: string;
    username: string;
    displayName: string;
    email: string;
    rocketchatUsername?: string;
  } | null;
  members: Array<{
    _id: string;
    username: string;
    displayName: string;
    email: string;
    rocketchatUsername?: string;
    gitlabUsername?: string;
  }>;
  settings: {
    standupTime: string;
    inactivityThresholdDays: number;
  };
}

/**
 * Full project dashboard data
 */
export interface ProjectDashboard {
  project: ProjectInfo;
  summary: DashboardSummary;
  repos: RepoStats[];
  issues: IssueSummary[];
  mergeRequests: MergeRequestSummary[];
  staleIssues: StaleIssue[];
  issuesByAssignee: IssueByAssignee[];
  recentCommits: RecentCommit[];
  recentReviews: RecentReview[];
}

/**
 * Issue detail with local user info
 */
export interface IssueAssignee {
  gitlabId: number;
  gitlabUsername: string;
  name: string;
  localUser: {
    id: string;
    username: string;
    displayName: string;
    rocketchatUsername?: string;
    canDM: boolean;
  } | null;
}

/**
 * Issue detail
 */
export interface IssueDetail {
  id: number;
  iid: number;
  title: string;
  description: string;
  state: 'opened' | 'closed';
  labels: string[];
  assignees: IssueAssignee[];
  author: GitLabUser;
  createdAt: string;
  updatedAt: string;
  dueDate: string | null;
  webUrl: string;
  timeEstimate: number;
  timeSpent: number;
  milestone: { id: number; title: string; dueDate: string | null } | null;
  isStale: boolean;
  daysSinceCreated: number;
  daysSinceUpdated: number;
}

/**
 * Related MR
 */
export interface RelatedMergeRequest {
  id: number;
  iid: number;
  title: string;
  state: string;
  sourceBranch: string;
  targetBranch: string;
  webUrl: string;
  isDraft: boolean;
  hasConflicts: boolean;
  mergedAt: string | null;
}

/**
 * Branch activity
 */
export interface BranchActivity {
  branch: string;
  commits: Array<{
    sha: string;
    title: string;
    committedAt: string;
    webUrl: string;
  }>;
  reviews: Array<{
    id: string;
    score: number;
    summary: string;
  }>;
}

/**
 * Issue detail response
 */
export interface IssueDetailResponse {
  issue: IssueDetail;
  mergeRequests: RelatedMergeRequest[];
  branchActivity: BranchActivity[];
  repo: {
    id: string;
    name: string;
    gitlabUrl: string;
    gitlabProjectId: number;
  };
}

/**
 * Get project dashboard
 */
export async function getProjectDashboard(
  projectId: string,
  options?: {
    issueState?: 'opened' | 'closed' | 'all';
    mrState?: 'opened' | 'closed' | 'merged' | 'all';
    staleThresholdDays?: number;
  }
): Promise<ProjectDashboard> {
  const params = new URLSearchParams();
  if (options?.issueState) params.append('issueState', options.issueState);
  if (options?.mrState) params.append('mrState', options.mrState);
  if (options?.staleThresholdDays) params.append('staleThresholdDays', options.staleThresholdDays.toString());

  const url = `/project-dashboard/${projectId}${params.toString() ? `?${params}` : ''}`;
  const response = await apiClient.get<ApiResponse<ProjectDashboard>>(url);
  return response.data.data as ProjectDashboard;
}

/**
 * Get issue detail
 */
export async function getIssueDetail(
  projectId: string,
  repoId: string,
  issueIid: number
): Promise<IssueDetailResponse> {
  const response = await apiClient.get<ApiResponse<IssueDetailResponse>>(
    `/project-dashboard/${projectId}/issue/${repoId}/${issueIid}`
  );
  return response.data.data as IssueDetailResponse;
}

/**
 * Issue comment from GitLab
 */
export interface IssueComment {
  id: number;
  body: string;
  author: {
    id: number;
    username: string;
    name: string;
    avatarUrl: string;
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * Get comments for an issue from GitLab
 */
export async function getIssueComments(
  projectId: string,
  repoId: string,
  issueIid: number,
  limit: number = 20
): Promise<IssueComment[]> {
  const response = await apiClient.get<ApiResponse<IssueComment[]>>(
    `/project-dashboard/${projectId}/issue/${repoId}/${issueIid}/comments?limit=${limit}`
  );
  return response.data.data as IssueComment[];
}

/**
 * Add comment to issue
 */
export async function addIssueComment(
  projectId: string,
  repoId: string,
  issueIid: number,
  body: string
): Promise<GatewayOutcome & { body: string }> {
  const response = await apiClient.post<ApiResponse<GatewayOutcome & { body: string }>>(
    `/project-dashboard/${projectId}/issue/${repoId}/${issueIid}/comment`,
    { body }
  );
  return response.data.data as GatewayOutcome & { body: string };
}

/**
 * Send DM to team member
 */
export async function sendTeamMemberDM(
  projectId: string,
  userId: string,
  content: string,
  context?: { issueTitle?: string }
): Promise<GatewayOutcome> {
  const response = await apiClient.post<ApiResponse<GatewayOutcome>>(
    `/project-dashboard/${projectId}/dm/${userId}`,
    { content, context }
  );
  return response.data.data as GatewayOutcome;
}

/** What the Action Gateway did with a write: sent, or recorded only (shadow), held, refused */
export interface GatewayOutcome {
  id?: number | string;
  decision: 'executed' | 'shadowed' | 'denied' | 'deduped' | 'deferred' | 'failed';
  denyReason?: string;
}

export function describeOutcome(outcome: GatewayOutcome): string | null {
  switch (outcome.decision) {
    case 'executed':
      return null;
    case 'shadowed':
      return 'Recorded only: this project is in shadow mode, so nothing was sent. See Shadow review.';
    case 'deferred':
      return 'Held until working hours.';
    case 'deduped':
      return 'Not sent again: the same message went out recently.';
    case 'denied':
      return `Not sent: ${outcome.denyReason || 'refused by policy'}`;
    default:
      return 'Sending failed. See the action log.';
  }
}
